import type { EmblaCarouselType } from 'embla-carousel';
import type { Dispatch, JSX, ReactElement, SetStateAction } from 'react';

import { cn } from '@repo/lib/cn';
import { weekStartsOnMonday } from '@repo/lib/constants/calendar-constants';
import { getWeekDates } from '@repo/lib/time/getWeekDates';
import { addWeeks, endOfWeek, format, isSameDay, isToday, startOfWeek, subWeeks } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CarouselApi } from '@/components/ui/carousel';

import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';

interface WeekRowProps {
  selected: Date;
  onSelect: (day: Date) => void;
  visibleWeeksOffset?: number;
  currentVisibleWeekDay?: Date;
  setCurrentVisibleWeekDay?: Dispatch<SetStateAction<Date>>;
  renderContextMenu?: (content: JSX.Element, day: Date, key: string) => JSX.Element;
  className?: string;
  setScrollPrev?: (scrollPrev: () => void) => void;
  setScrollNext?: (scrollNext: () => void) => void;
}

export default function WeekCards(weekCardsProps: WeekRowProps) {
  const { selected = new Date(), onSelect, renderContextMenu, visibleWeeksOffset, setCurrentVisibleWeekDay, className, setScrollPrev, setScrollNext } = weekCardsProps;
  const selectedScrollSnapRef = useRef<number | undefined>(visibleWeeksOffset);
  const [weeks, setWeeks] = useState<Date[][]>([]);
  const [api, setApi] = useState<CarouselApi>();

  const handleOnClick = useCallback(
    (day: Date) => {
      onSelect(day);
    },
    [onSelect]
  );

  useEffect(() => {
    if (weeks.length !== 0) {
      return;
    }

    if (!visibleWeeksOffset) {
      setWeeks([getWeekDates(new Date())]);
    } else {
      const weeks: Date[][] = [];
      const referenceStartOfWeek = startOfWeek(new Date(), {
        weekStartsOn: weekStartsOnMonday,
      });
      for (let i = -visibleWeeksOffset; i <= visibleWeeksOffset; i++) {
        const weekStart = addWeeks(referenceStartOfWeek, i);
        weeks.push(getWeekDates(weekStart));
      }
      setWeeks(weeks);
    }
  }, [visibleWeeksOffset, weeks.length]);

  const setCurrentVisibleWeekDayCn = useCallback(
    (emblaEventApi: EmblaCarouselType) => {
      const prevSelectedScrollSnap = selectedScrollSnapRef.current;
      const isPrevSelectedScrollSnap = prevSelectedScrollSnap !== undefined;
      const newSelectedScrollSnap = emblaEventApi.selectedScrollSnap();

      if (isPrevSelectedScrollSnap && prevSelectedScrollSnap < newSelectedScrollSnap) {
        setCurrentVisibleWeekDay?.((prev) =>
          endOfWeek(addWeeks(prev, 1), {
            weekStartsOn: weekStartsOnMonday,
          })
        );
      }
      if (isPrevSelectedScrollSnap && prevSelectedScrollSnap > newSelectedScrollSnap) {
        setCurrentVisibleWeekDay?.((prev) =>
          endOfWeek(subWeeks(prev, 1), {
            weekStartsOn: weekStartsOnMonday,
          })
        );
      }
      if (isPrevSelectedScrollSnap) {
        selectedScrollSnapRef.current = newSelectedScrollSnap;
      }
    },
    [setCurrentVisibleWeekDay]
  );

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  useEffect(() => {
    api?.on('select', setCurrentVisibleWeekDayCn);

    if (setScrollPrev) {
      setScrollPrev(scrollPrev);
    }
    if (setScrollNext) {
      setScrollNext(scrollNext);
    }

    return () => {
      api?.off('select', setCurrentVisibleWeekDayCn);
    };
  }, [api, setCurrentVisibleWeekDayCn, setScrollPrev, setScrollNext, scrollPrev, scrollNext]);

  useEffect(() => {
    api?.scrollTo(visibleWeeksOffset ?? 0, true);
  }, [api, visibleWeeksOffset]);

  const content = useMemo(() => {
    if (visibleWeeksOffset) {
      return (
        <Carousel setApi={setApi} className={'w-full'} opts={{ align: 'center' }}>
          <CarouselContent className={'p-1'}>
            {weeks.map((week, index) => (
              <CarouselItem key={index}>
                <WeekCardsRow week={week} selected={selected} className={className} onClick={handleOnClick} renderContextMenu={renderContextMenu} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      );
    } else if (weeks[0]) {
      return <WeekCardsRow week={weeks[0]} selected={selected} className={className} onClick={handleOnClick} renderContextMenu={renderContextMenu} />;
    }
    return null;
  }, [className, handleOnClick, renderContextMenu, selected, visibleWeeksOffset, weeks]);

  return <div className={'relative m-auto flex w-full items-center gap-1 overflow-hidden px-0'}>{content}</div>;
}

interface WeekCardsProps {
  week: Date[];
  selected: Date;
  onClick: (day: Date) => void;
  renderContextMenu?: (children: ReactElement, day: Date, key: string) => ReactElement;
  className?: string;
}

export function WeekCardsRow(weekCardsRowProps: WeekCardsProps) {
  const { week, renderContextMenu, selected, onClick, ...props } = weekCardsRowProps;
  return (
    <div className={'flex w-full gap-1'}>
      {week.map((day) => (
        <WeekCard key={day.toString()} day={day} renderContextMenu={renderContextMenu} selected={selected} onClick={onClick} {...props} />
      ))}
    </div>
  );
}

interface WeekCardProps {
  day: Date;
  selected: Date;
  onClick: (day: Date) => void;
  renderContextMenu?: (children: ReactElement, day: Date, key: string) => ReactElement;
  className?: string;
}

function WeekCard(weekCardProps: WeekCardProps) {
  const { day, selected, renderContextMenu, onClick, className, ...props } = weekCardProps;
  const elKey = day.toString();
  const weekDay = format(day, 'cccccc');
  const monthDate = format(day, 'dd');
  const today = isToday(day);
  const sameDay = isSameDay(day, selected);

  const content = (
    <button
      key={'weekDay_' + elKey}
      aria-checked={sameDay}
      data-selected={sameDay}
      className={cn(
        'relative overflow-hidden outline-focus-ring data-[selected=false]:border-border-primary data-[selected=false]:bg-input hover:data-[selected=false]:bg-accent active:data-[selected=false]:bg-accent/80 data-[selected=true]:border-gray data-[selected=true]:bg-gray data-[selected=true]:text-gray-foreground flex w-11 min-w-7 cursor-pointer flex-col items-center gap-0.5 rounded-md border px-3 py-1 opacity-100 outline-0 transition-all duration-150 select-none focus-visible:outline-3 disabled:opacity-60 hover:data-[selected=true]:opacity-80 inset-shadow-xs inset-shadow-white/10 dark:inset-shadow-white/5 before:absolute before:inset-0 before:bg-linear-to-t before:from-transparent before:to-white/15 dark:before:to-white/6 before:pointer-events-none',
        className
      )}
      onClick={() => onClick(day)}
      {...props}
    >
      <small className={cn('text-[10px] leading-4 tracking-tight', sameDay ? 'text-gray-foreground' : 'text-muted-foreground')}>{weekDay}</small>
      <span className={cn('text-sm', sameDay ? 'text-gray-foreground' : 'text-accent-foreground')}>{monthDate}</span>
      {today && <span className={cn('absolute bottom-0.5 h-1 min-h-1 w-1 min-w-1 rounded-full', sameDay ? 'bg-gray-foreground' : 'bg-muted-foreground')} />}
    </button>
  );
  if (!renderContextMenu) {
    return content;
  }
  return renderContextMenu(content, day, 'contextDay_' + elKey);
}
